/**
 * Voice Prompt Service for Agent Daredevil
 * Provides contextual voice prompts during betting flow using ElevenLabs TTS
 */

export interface VoicePromptContext {
  step: 'initial' | 'confirmation' | 'discourage' | 'processing' | 'sign' | 'error' | 'success'
  userMessage?: string
  betAmount?: number
  betCurrency?: string
  competitor?: string
  attempts?: number
}

export class VoicePromptService {
  private isEnabled: boolean = true
  private currentContext: VoicePromptContext | null = null

  constructor() {
    // Check if TTS helper is available
    this.isEnabled = typeof window !== 'undefined' && 
                    window.speakText !== undefined
  }

  /**
   * Main method to trigger voice prompts based on context
   */
  async playPrompt(context: VoicePromptContext): Promise<void> {
    if (!this.isEnabled) {
      console.log('🎤 Voice prompts disabled - TTS not available')
      return
    }

    // Stop any current speech before starting new prompt
    if (window.stopSpeaking) {
      window.stopSpeaking()
    }

    // Add a small delay to ensure previous speech is stopped
    await new Promise(resolve => setTimeout(resolve, 200))

    this.currentContext = context
    const prompt = this.getPromptText(context)
    
    if (prompt) {
      console.log(`🎤 Playing voice prompt [${context.step}]:`, prompt)
      
      // Use ElevenLabs TTS with natural conversational speed
      if (window.speakText) {
        try {
          await window.speakText(prompt, {
            playbackRate: 1.4, // Natural conversational speed
            stability: 0.3, // Slightly more dynamic for character
            similarity_boost: 0.85 // High similarity for consistency
          })
          
          // Add a pause after each prompt for natural conversation flow
          await new Promise(resolve => setTimeout(resolve, 500))
          
        } catch (error) {
          console.error('🎤 Voice prompt error:', error)
        }
      }
    }
  }

  /**
   * Get the appropriate prompt text based on context
   */
  private getPromptText(context: VoicePromptContext): string {
    switch (context.step) {
      case 'initial':
        return this.getInitialPrompt(context)
      
      case 'confirmation':
        return this.getConfirmationPrompt(context)
      
      case 'discourage':
        return this.getDiscouragePrompt(context)
      
      case 'processing':
        return this.getProcessingPrompt(context)
      
      case 'sign':
        return this.getSignPrompt(context)
      
      case 'error':
        return this.getErrorPrompt(context)
      
      case 'success':
        return this.getSuccessPrompt(context)
      
      default:
        return ''
    }
  }

  /**
   * Initial betting trigger prompts
   */
  private getInitialPrompt(context: VoicePromptContext): string {
    const prompts = [
      "Oh alright, you wanna put your money where your mouth is huh?",
      "So you're feeling lucky today? Let's see what you got.",
      "Betting time? This should be interesting.",
      "You want to make a bet? Bold move, let's do this.",
      "Oh, someone's confident! Ready to back that up?"
    ]
    
    return this.getRandomPrompt(prompts)
  }

  /**
   * Confirmation and discouragement prompts
   */
  private getConfirmationPrompt(context: VoicePromptContext): string {
    const amount = context.betAmount || 0
    const currency = context.betCurrency || 'USD'
    const competitor = context.competitor || 'your pick'
    
    return `Are you sure you want to bet ${amount} ${currency} on ${competitor}? Think about it for a second.`
  }

  /**
   * Discouragement prompts (responsible gambling)
   */
  private getDiscouragePrompt(context: VoicePromptContext): string {
    const prompts = [
      "Look, gambling can be addictive. Are you absolutely sure about this?",
      "Remember, only bet what you can afford to lose. You sure about this?",
      "Hey, just making sure you're not betting the rent money here.",
      "Pause for a second - is this really a smart financial decision?",
      "Just checking - you're betting responsibly, right? Only what you can afford?",
      "Real talk - gambling should be fun, not financial stress. You good with this amount?"
    ]
    
    return this.getRandomPrompt(prompts)
  }

  /**
   * Processing prompts
   */
  private getProcessingPrompt(context: VoicePromptContext): string {
    const prompts = [
      "Alright, let me set that up for you.",
      "Processing your bet now.",
      "Setting up your transaction.",
      "Getting everything ready for you.",
      "Alright, preparing your bet."
    ]
    
    return this.getRandomPrompt(prompts)
  }

  /**
   * Transaction signing prompts
   */
  private getSignPrompt(context: VoicePromptContext): string {
    const prompts = [
      "Alright, just sign that transaction to lock it in.",
      "Time to sign - this will finalize your bet.",
      "Sign the transaction when you're ready.",
      "Almost there - just need your signature to confirm.",
      "Hit that sign button to make it official."
    ]
    
    return this.getRandomPrompt(prompts)
  }

  /**
   * Error/retry prompts
   */
  private getErrorPrompt(context: VoicePromptContext): string {
    const attempts = context.attempts || 1
    
    if (attempts === 1) {
      const prompts = [
        "Let's try that again.",
        "Something went wrong, let's give it another shot.",
        "Hmm, that didn't work. Want to try again?",
        "Technical difficulties. Let's retry this.",
        "Oops, let's run that back."
      ]
      return this.getRandomPrompt(prompts)
    } else {
      const prompts = [
        "Still having issues. Check your wallet connection?",
        "This is getting frustrating. Maybe check your internet?",
        "Third time's the charm? Let's try once more.",
        "Having some technical difficulties here."
      ]
      return this.getRandomPrompt(prompts)
    }
  }

  /**
   * Success prompts
   */
  private getSuccessPrompt(context: VoicePromptContext): string {
    const prompts = [
      "Nice, you're good to go! Share the QR code to dare your friends. Good luck, nerd!",
      "Boom! Bet's locked in. Show that QR code to your friends and watch them sweat!",
      "Perfect! Your bet is live. Time to start trash talking with that QR code!",
      "All set! Share that QR code and let's see who's got the guts to take you on!",
      "Bet confirmed! Now go flex that QR code and dare someone to match your energy!",
      "Done deal! Use that QR code to challenge your friends. May the odds be with you!"
    ]
    
    return this.getRandomPrompt(prompts)
  }

  /**
   * Get random prompt from array for variety
   */
  private getRandomPrompt(prompts: string[]): string {
    return prompts[Math.floor(Math.random() * prompts.length)]
  }

  /**
   * Check if user response indicates confirmation
   */
  isConfirmation(userResponse: string): boolean {
    const confirmWords = [
      'yes', 'yeah', 'yep', 'confirm', 'ok', 'okay', 'sure', 'absolutely',
      'definitely', 'lets go', 'do it', 'go ahead', 'proceed',
      'affirmative', 'correct', 'right', 'true', 'indeed', 'of course'
    ]
    
    const response = userResponse.toLowerCase().trim()
    return confirmWords.some(word => response.includes(word))
  }

  /**
   * Check if user response indicates cancellation
   */
  isCancellation(userResponse: string): boolean {
    const cancelWords = [
      'no', 'nope', 'cancel', 'stop', 'abort', 'nevermind', 'never mind',
      'forget it', 'back out', 'changed my mind', 'not sure', 'maybe not',
      'on second thought', 'actually no', 'nah'
    ]
    
    const response = userResponse.toLowerCase().trim()
    return cancelWords.some(word => response.includes(word))
  }

  /**
   * Enable/disable voice prompts
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled
    console.log(`🎤 Voice prompts ${enabled ? 'enabled' : 'disabled'}`)
  }

  /**
   * Get current context
   */
  getCurrentContext(): VoicePromptContext | null {
    return this.currentContext
  }
}

// Global instance
export const voicePromptService = new VoicePromptService()

// Add to window for debugging
if (typeof window !== 'undefined') {
  (window as any).voicePromptService = voicePromptService
}
